(() => {
  'use strict';

  const intervalRegistry = new WeakMap();
  const shopPayObserverRegistry = new WeakMap();
  let shopPayDocumentObserver;

  const SHOP_PAY_SELECTOR = '.maison-pdp-installments shopify-payment-terms';
  const SHOP_PAY_STYLE_ID = 'maison-shop-pay-style';
  const SHOP_PAY_SHADOW_STYLES = `
    #shopify-installments {
      font-family: inherit;
      color: inherit;
    }
    #shopify-installments p {
      display: grid;
      row-gap: .35rem;
      margin: 0;
      font-family: inherit;
      font-size: inherit;
      font-weight: inherit;
      line-height: inherit;
      letter-spacing: inherit;
      color: inherit;
    }
    #shopify-installments-content {
      padding-right: 0;
    }
    #shopify-installments-content .font-bold {
      font-weight: 600;
      color: #514d46;
    }
    .maison-shop-pay__primary {
      display: block;
      text-wrap: balance;
    }
    .maison-shop-pay__secondary {
      display: flex;
      align-items: center;
      gap: .7rem;
      min-height: 1.8rem;
      margin-left: 0;
      white-space: nowrap;
    }
    .maison-shop-pay__with {
      color: #8a857c;
    }
    #shopify-installments .text-purple-primary {
      color: #16140f !important;
      vertical-align: -.16em;
    }
    #shopify-installments .text-purple-primary svg {
      width: 4.8rem;
      height: auto;
    }
    #shopify-installments-cta {
      margin: 0;
      padding: 0 0 .1rem;
      border: 0;
      background: transparent;
      font-family: inherit;
      font-size: .95em;
      font-weight: 400;
      line-height: inherit;
      letter-spacing: .07rem;
      color: #514d46;
      text-decoration-line: underline;
      text-decoration-thickness: .1rem;
      text-underline-offset: .3rem;
      text-transform: uppercase;
    }
    #shopify-installments-cta:hover {
      color: #16140f;
    }
    #shopify-installments-cta:focus-visible {
      outline: .1rem solid #16140f;
      outline-offset: .3rem;
      text-decoration: none;
    }
    @media (max-width: 749px) {
      #shopify-installments-cta {
        font-size: .9em;
        letter-spacing: .08rem;
      }
    }
    @media (max-width: 340px) {
      .maison-shop-pay__primary {
        font-size: .9em;
        line-height: 1.65;
        letter-spacing: .03rem;
      }
    }
  `;

  function addShopPayShadowStyles(shadowRoot) {
    if (shadowRoot.getElementById(SHOP_PAY_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = SHOP_PAY_STYLE_ID;
    style.textContent = SHOP_PAY_SHADOW_STYLES;
    shadowRoot.appendChild(style);
  }

  function structureShopPayTerms(shadowRoot) {
    const content = shadowRoot.querySelector('#shopify-installments-content');
    const button = shadowRoot.querySelector('#shopify-installments-cta');
    if (!content || !button) return;

    const secondary = shadowRoot.querySelector('.maison-shop-pay__secondary');
    if (content.dataset.maisonShopPayStructured === 'true' && secondary?.contains(button)) {
      if (button.textContent.trim() !== 'DETAILS') button.textContent = 'DETAILS';
      return;
    }

    if (secondary && !secondary.contains(button)) secondary.remove();
    const logo = content.querySelector('.text-purple-primary');
    if (!logo) return;

    const precedingNode = logo.previousSibling;
    if (precedingNode?.nodeType === Node.TEXT_NODE) {
      const match = precedingNode.textContent.match(/^(.*?)(?:\s+with\s*)$/i);
      if (match) precedingNode.textContent = match[1].replace(/\s+$/, '');
    }

    const secondaryRow = document.createElement('span');
    secondaryRow.className = 'maison-shop-pay__secondary';
    const withLabel = document.createElement('span');
    withLabel.className = 'maison-shop-pay__with';
    withLabel.textContent = 'with';

    content.classList.add('maison-shop-pay__primary');
    content.dataset.maisonShopPayStructured = 'true';
    secondaryRow.append(withLabel, logo, button);
    content.after(secondaryRow);
    button.textContent = 'DETAILS';
  }

  function enhanceShopPayTerm(shadowRoot) {
    addShopPayShadowStyles(shadowRoot);
    structureShopPayTerms(shadowRoot);
  }

  function styleShopPayTerm(term, attempt = 0) {
    if (!term?.isConnected) return;
    const shadowRoot = term.shadowRoot;

    if (!shadowRoot) {
      if (attempt < 80) window.setTimeout(() => styleShopPayTerm(term, attempt + 1), 50);
      return;
    }

    enhanceShopPayTerm(shadowRoot);
    if (shopPayObserverRegistry.has(term)) return;

    const observer = new MutationObserver(() => enhanceShopPayTerm(shadowRoot));
    observer.observe(shadowRoot, { childList: true, characterData: true, subtree: true });
    shopPayObserverRegistry.set(term, observer);
  }

  function initialiseShopPay(root = document) {
    if (root instanceof Element && root.matches(SHOP_PAY_SELECTOR)) styleShopPayTerm(root);
    root.querySelectorAll?.(SHOP_PAY_SELECTOR).forEach((term) => styleShopPayTerm(term));

    if (root !== document || shopPayDocumentObserver || !document.body) return;
    shopPayDocumentObserver = new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches(SHOP_PAY_SELECTOR)) styleShopPayTerm(node);
          node.querySelectorAll?.(SHOP_PAY_SELECTOR).forEach((term) => styleShopPayTerm(term));
        });
      });
    });
    shopPayDocumentObserver.observe(document.body, { childList: true, subtree: true });
  }

  function initialiseAnnouncement(root = document) {
    root.querySelectorAll('[data-maison-announcement]').forEach((bar) => {
      if (bar.dataset.maisonReady === 'true') return;

      const messages = [...bar.querySelectorAll('[data-maison-announcement-message]')];
      if (!messages.length) return;

      bar.dataset.maisonReady = 'true';
      let activeIndex = Math.max(messages.findIndex((message) => message.classList.contains('is-active')), 0);
      let manuallyPaused = false;
      const delay = Math.max(Number.parseInt(bar.dataset.interval, 10) || 6000, 3000);
      const toggle = bar.querySelector('[data-maison-announcement-toggle]');
      const toggleLabel = bar.querySelector('[data-maison-announcement-toggle-label]');

      const show = (nextIndex) => {
        activeIndex = (nextIndex + messages.length) % messages.length;
        messages.forEach((message, index) => {
          const active = index === activeIndex;
          message.classList.toggle('is-active', active);
          message.setAttribute('aria-hidden', String(!active));
        });
      };

      const stop = () => {
        const timer = intervalRegistry.get(bar);
        if (timer) window.clearInterval(timer);
        intervalRegistry.delete(bar);
      };

      const start = () => {
        stop();
        if (messages.length < 2 || manuallyPaused || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        intervalRegistry.set(bar, window.setInterval(() => show(activeIndex + 1), delay));
      };

      const setPaused = (paused) => {
        manuallyPaused = paused;
        toggle?.setAttribute('aria-pressed', String(paused));
        toggle?.setAttribute('aria-label', paused ? 'Play announcements' : 'Pause announcements');
        if (toggleLabel) toggleLabel.textContent = paused ? 'PLAY' : 'PAUSE';
        if (paused) stop(); else start();
      };

      show(activeIndex);
      bar.querySelector('[data-maison-announcement-prev]')?.addEventListener('click', () => {
        show(activeIndex - 1);
        start();
      });
      bar.querySelector('[data-maison-announcement-next]')?.addEventListener('click', () => {
        show(activeIndex + 1);
        start();
      });
      toggle?.addEventListener('click', () => setPaused(!manuallyPaused));
      bar.addEventListener('mouseenter', stop);
      bar.addEventListener('mouseleave', start);
      bar.addEventListener('focusin', stop);
      bar.addEventListener('focusout', start);
      start();
    });
  }

  function initialiseFooter(root = document) {
    root.querySelectorAll('[data-maison-scroll-top]').forEach((button) => {
      if (button.dataset.maisonReady === 'true') return;
      button.dataset.maisonReady = 'true';
      button.addEventListener('click', () => {
        window.scrollTo({
          top: 0,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      });
    });

    root.querySelectorAll('[data-maison-localization-select]').forEach((select) => {
      if (select.dataset.maisonReady === 'true') return;
      select.dataset.maisonReady = 'true';
      select.addEventListener('change', () => select.form?.submit());
    });
  }

  function initialise(root = document) {
    initialiseAnnouncement(root);
    initialiseFooter(root);
    initialiseShopPay(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-maison-announcement]').forEach((bar) => {
      const timer = intervalRegistry.get(bar);
      if (timer) window.clearInterval(timer);
      intervalRegistry.delete(bar);
    });
    event.target.querySelectorAll(SHOP_PAY_SELECTOR).forEach((term) => {
      shopPayObserverRegistry.get(term)?.disconnect();
      shopPayObserverRegistry.delete(term);
    });
  });
})();
